import React, { useMemo } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  LabelList,
} from 'recharts';
import { format, parseISO } from 'date-fns';
import { useThemeStore } from '../../stores';
import { cn } from '../../utils/helpers';
import { getChartPalette } from './chartTheme';
import type { InsightsTrendPoint } from '../../types';

interface InsightsTrendChartProps {
  points: InsightsTrendPoint[];
  className?: string;
}

const SERIES = [
  { key: 'sessions', label: 'Checkout Sessions', slot: 0 },
  { key: 'identifiedLeads', label: 'Leads Captured', slot: 1 },
  { key: 'conversions', label: 'Orders Placed', slot: 2 },
] as const;

const CustomTooltip: React.FC<{
  active?: boolean;
  payload?: Array<{ value: number; color: string; dataKey: string; payload: InsightsTrendPoint }>;
  label?: string;
  isDark: boolean;
}> = ({ active, payload, label, isDark }) => {
  if (!active || !payload?.length) return null;

  const colors = getChartPalette(isDark);
  const point = payload[0]?.payload;

  return (
    <div
      className="rounded-lg px-4 py-3 shadow-elevated border min-w-[190px]"
      style={{ backgroundColor: colors.tooltipBg, borderColor: colors.tooltipBorder }}
    >
      <p className="text-sm font-medium mb-2" style={{ color: colors.tooltipText }}>
        {label}
      </p>
      {SERIES.map((series) => {
        const entry = payload.find((p) => p.dataKey === series.key);
        if (!entry) return null;
        return (
          <div key={series.key} className="flex items-center gap-2 text-sm">
            <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: entry.color }} />
            <span className="flex-1" style={{ color: colors.axis }}>
              {series.label}
            </span>
            <span className="font-semibold tabular-nums" style={{ color: colors.tooltipText }}>
              {entry.value}
            </span>
          </div>
        );
      })}
      {point && (
        <div
          className="mt-2 pt-2 border-t flex items-center justify-between text-xs"
          style={{ borderColor: colors.tooltipBorder, color: colors.axis }}
        >
          <span>Conversion</span>
          <span className="font-semibold tabular-nums" style={{ color: colors.tooltipText }}>
            {point.conversionRate}%
          </span>
        </div>
      )}
    </div>
  );
};

const CustomLegend: React.FC<{ payload?: Array<{ value: string; color: string }> }> = ({ payload }) => {
  if (!payload?.length) return null;

  return (
    <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 mt-3">
      {payload.map((entry) => (
        <div key={entry.value} className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-full" style={{ backgroundColor: entry.color }} />
          <span className="text-sm text-secondary-500 dark:text-secondary-400">
            {SERIES.find((s) => s.key === entry.value)?.label || entry.value}
          </span>
        </div>
      ))}
    </div>
  );
};

/**
 * Labels only the final point of a series. Series identity therefore never rests
 * on colour alone, which is what lets this palette use its 6–8 CVD band slots.
 */
const buildEndLabel =
  (lastIndex: number, color: string) =>
  ({ x, y, value, index }: { x?: number; y?: number; value?: number; index?: number }) => {
    if (index !== lastIndex || x === undefined || y === undefined) return null;
    return (
      <text
        x={x}
        y={y - 10}
        textAnchor="end"
        fontSize={12}
        fontWeight={600}
        fill={color}
        className="tabular-nums"
      >
        {value}
      </text>
    );
  };

export const InsightsTrendChart: React.FC<InsightsTrendChartProps> = ({ points, className }) => {
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';
  const colors = getChartPalette(isDark);

  const chartData = useMemo(
    () =>
      points.map((point) => ({
        ...point,
        label: format(parseISO(point.date), 'MMM d'),
      })),
    [points]
  );

  // Long ranges get fewer x-axis ticks so labels never collide.
  const tickInterval = chartData.length > 45 ? Math.ceil(chartData.length / 12) : 'preserveStartEnd';

  return (
    <div className={cn('w-full min-h-[320px]', className)}>
      <ResponsiveContainer width="100%" height="100%">
        {/* Right margin leaves room for the end-of-line value labels */}
        <AreaChart data={chartData} margin={{ top: 12, right: 20, left: -12, bottom: 0 }}>
          <defs>
            {SERIES.map((series) => (
              <linearGradient
                key={series.key}
                id={`insights-${series.key}-${isDark ? 'dark' : 'light'}`}
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop offset="0%" stopColor={colors.series[series.slot]} stopOpacity={0.22} />
                <stop offset="100%" stopColor={colors.series[series.slot]} stopOpacity={0} />
              </linearGradient>
            ))}
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke={colors.grid} vertical={false} />
          <XAxis
            dataKey="label"
            tick={{ fill: colors.axis, fontSize: 12 }}
            tickLine={false}
            axisLine={{ stroke: colors.grid }}
            interval={tickInterval as number | 'preserveStartEnd'}
            tickMargin={8}
            minTickGap={16}
          />
          <YAxis
            tick={{ fill: colors.axis, fontSize: 12 }}
            tickLine={false}
            axisLine={false}
            allowDecimals={false}
            tickMargin={4}
            width={44}
          />
          <Tooltip
            content={<CustomTooltip isDark={isDark} />}
            cursor={{ stroke: colors.axis, strokeDasharray: '4 4', strokeOpacity: 0.5 }}
          />
          <Legend content={<CustomLegend />} />
          {SERIES.map((series) => (
            <Area
              key={series.key}
              type="monotone"
              dataKey={series.key}
              stroke={colors.series[series.slot]}
              strokeWidth={2}
              fill={`url(#insights-${series.key}-${isDark ? 'dark' : 'light'})`}
              activeDot={{
                r: 5,
                fill: colors.series[series.slot],
                stroke: colors.surface,
                strokeWidth: 2,
              }}
            >
              <LabelList
                dataKey={series.key}
                content={buildEndLabel(chartData.length - 1, colors.series[series.slot]) as never}
              />
            </Area>
          ))}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

export default InsightsTrendChart;
